import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from "@/components/ui/item";
import { formatCurrencyWithCode } from "@/helpers/currency-formatter";

import type {
  PurchaseRequestDecisionItem,
  PurchaseRequestDecisionSupplierSummary,
} from "../hooks/use-purchase-request-decision-items";

interface PurchaseRequestDecisionItemsCardsProps {
  items: PurchaseRequestDecisionItem[];
}

const formatDiff = (diff: number | null) => {
  if (diff == null) return "";
  if (diff === 0) return "0.0%";
  const sign = diff > 0 ? "+" : "";
  return `${sign}${diff.toFixed(1)}%`;
};

const formatTerm = (value: string | null | undefined, suffix?: string) => {
  if (!value || value === "null") return "S/E";
  return suffix ? `${value} ${suffix}` : value;
};

const formatMoneyWithCode = (
  value: number | null | undefined,
  currency: string,
) => {
  if (value == null) return "S/E";
  return formatCurrencyWithCode(value, currency);
};

export default function PurchaseRequestDecisionItemsCards({
  items,
}: PurchaseRequestDecisionItemsCardsProps) {
  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
        No hay productos seleccionados para mostrar en esta vista.
      </div>
    );
  }

  return (
    <div className="rounded-md border border-border/80 bg-background">
      <div className="divide-y">
        {items.map((item) => (
          <div
            key={item.requestItemId}
            className="space-y-2 px-3 py-3 sm:px-4 sm:py-3.5"
          >
            <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
              <div className="min-w-0 space-y-0.5">
                <h3 className="break-words text-sm font-semibold text-foreground">
                  {item.description}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {item.quantity} {item.unit}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                <Badge
                  variant="outline"
                  className="h-auto max-w-full whitespace-normal break-words bg-muted/40 py-1 text-foreground sm:max-w-[24rem]"
                >
                  {item.selectedSupplierName}
                </Badge>
                <span className="inline-flex items-center rounded-sm bg-primary/10 px-2 py-0.5 font-semibold text-primary tabular-nums">
                  {formatMoneyWithCode(
                    item.selectedUnitPriceOriginal,
                    item.selectedCurrency,
                  )}
                </span>
                {item.selectedCurrency !== "ARS" && item.selectedUnitPriceArs != null && (
                  <span className="text-xs text-muted-foreground tabular-nums">
                    ({formatMoneyWithCode(item.selectedUnitPriceArs, "ARS")})
                  </span>
                )}
              </div>
            </div>

            {item.selectedMissingExchangeRate && (
              <p className="text-xs text-amber-600">
                Falta tipo de cambio para convertir este precio.
              </p>
            )}

            <Accordion type="single" collapsible className="rounded-sm border px-2">
              <AccordionItem
                value={`alternatives-${item.requestItemId}`}
                className="border-b-0"
              >
                <AccordionTrigger className="py-1 text-xs hover:no-underline">
                  Otros precios ({item.alternatives.length})
                </AccordionTrigger>
                <AccordionContent className="space-y-1 pb-2">
                  {item.alternatives.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      No hay precios alternativos para este producto.
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {item.alternatives.map((alternative, index) => (
                        <div
                          key={`${item.requestItemId}-${alternative.supplierId}-${index}`}
                          className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1 rounded-sm px-1.5 py-1 text-xs"
                        >
                          <div className="min-w-0">
                            <p className="font-medium break-words">
                              {alternative.supplierName}
                            </p>
                            <div className="flex flex-wrap items-center gap-2 text-muted-foreground">
                              {alternative.currency !== "ARS" && (
                                <span>{alternative.currency}</span>
                              )}
                              {alternative.diffVsSelectedPct != null && (
                                <span>
                                  Dif: {formatDiff(alternative.diffVsSelectedPct)}
                                </span>
                              )}
                              {alternative.missingExchangeRate && (
                                <span className="text-amber-600">
                                  Sin tipo de cambio
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold tabular-nums">
                              {formatMoneyWithCode(
                                alternative.unitPriceOriginal,
                                alternative.currency,
                              )}
                            </p>
                            {alternative.currency !== "ARS" && (
                              <p className="text-muted-foreground tabular-nums">
                                {formatMoneyWithCode(
                                  alternative.unitPriceArs,
                                  "ARS",
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        ))}
      </div>
    </div>
  );
}

interface PurchaseRequestDecisionSummaryCardsProps {
  selectedSuppliers: PurchaseRequestDecisionSupplierSummary[];
  totalArs: number;
}

export function PurchaseRequestDecisionSummaryCards({
  selectedSuppliers,
  totalArs,
}: PurchaseRequestDecisionSummaryCardsProps) {
  if (selectedSuppliers.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
        No hay proveedores seleccionados para resumir condiciones.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {selectedSuppliers.map((supplier) => (
          <Item
            key={supplier.supplierId}
            variant="outline"
            size="sm"
            className="w-full"
          >
            <ItemContent>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <ItemTitle className="break-words">{supplier.supplierName}</ItemTitle>
                  <ItemDescription className="line-clamp-none text-xs">
                    {supplier.selectedItemsCount} ítems seleccionados
                  </ItemDescription>
                </div>
                <div className="text-right">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Subtotal ARS
                  </p>
                  <p className="font-semibold tabular-nums">
                    {formatMoneyWithCode(supplier.selectedSubtotalArs, "ARS")}
                  </p>
                </div>
              </div>

              <div className="mt-2 grid gap-1.5 text-xs sm:grid-cols-2">
                <p className="text-muted-foreground">
                  Forma de pago:{" "}
                  <span className="font-medium text-foreground">
                    {formatTerm(supplier.paymentMethod)}
                  </span>
                </p>
                <p className="text-muted-foreground">
                  Plazo de pago:{" "}
                  <span className="font-medium text-foreground">
                    {formatTerm(supplier.paymentTermDays, "días")}
                  </span>
                </p>
                <p className="text-muted-foreground">
                  Plazo de entrega:{" "}
                  <span className="font-medium text-foreground">
                    {formatTerm(supplier.deliveryTime)}
                  </span>
                </p>
                <p className="text-muted-foreground">
                  Validez oferta:{" "}
                  <span className="font-medium text-foreground">
                    {formatTerm(supplier.validityText)}
                  </span>
                </p>
              </div>
            </ItemContent>
          </Item>
        ))}
      </div>

      <Item
        variant="outline"
        size="default"
        className="w-full bg-muted/30"
      >
        <ItemContent>
          <ItemTitle>Total final</ItemTitle>
          <ItemDescription className="line-clamp-none text-xs">
            Total consolidado de la selección aprobable
          </ItemDescription>
        </ItemContent>
        <div className="flex items-center">
          <span className="text-xl font-bold tabular-nums sm:text-2xl">
            {formatMoneyWithCode(totalArs, "ARS")}
          </span>
        </div>
      </Item>
    </div>
  );
}
